import { Alert, Button, Divider, FlexView, Form, Title } from '@facile-it/shark-morph/components'
import { FormRow } from '@facile-it/shark-morph/components/Form/FormRow'
import * as O from 'fp-ts/Option'
import { map } from 'fp-ts/Option'
import { identity, pipe } from 'fp-ts/function'
import * as React from 'react'
import type { ResourceRejected } from '../../../types/Resource'
import buildTestSelector from '../../Env/testSelector/buildTestSelector'
import { masterStateBuilder } from '../../MasterStateHandler/masterStateBuilder'
import { fieldsMerger } from '../FormSlave'
import type { FormWorker } from '../FormWorker'
import type { Unit } from '../Unit'
import type { formResource, helper, Structure } from '../formBuilder'
import { UnitFormRenderer } from './UnitFormRenderer'
import { UnitHtmlRenderer } from './UnitHtmlRenderer'

export namespace FromRenderer {
    export interface Props {
        formName: Form.Props['name']
        onSubmit: Form.Props['onSubmit']
        onChange: (name: string) => UnitFormRenderer.Props<Unit.UnionFields['c']>['onChange']
        fields: FormWorker.InternalValueFields
        unitsForm: FormWorker.Props['unitsForm']
        unitsHtml: FormWorker.Props['unitsHtml']
        fieldsValidatorResult: FormWorker.InternalValidatorFields
        structure: FormWorker.Props['structure']
        helper: FormWorker.Props['helper']
        submit: FormWorker.Props['submit']
        resource: FormWorker.Props['resource']
        employeesStatus: FormWorker.EmployeesStatus
        formStyle?: FormWorker.Props['formStyle']
    }
}

const flexViewProps = (row: Structure<string>[0]['rows'][0]): FlexView.Props =>
    pipe(
        O.fromNullable(row.view),
        O.getOrElse<FlexView.Props>(() => ({ gutters: { lr: 'std' } }))
    )

const findUnit = (mergedUnit: { [name: string]: Unit.UnitUnion }, name: string): Unit.UnitUnion => {
    return mergedUnit[name]
}

const renderRow = (
    row: Structure<string>[0]['rows'][0],
    unitsForm: FormWorker.Props['unitsForm'],
    unitsHtml: FormWorker.Props['unitsHtml'],
    fields: FormWorker.InternalValueFields,
    fieldsValidatorResult: FormWorker.InternalValidatorFields,
    onChange: (name: string) => UnitFormRenderer.Props<Unit.UnionFields['c']>['onChange'],
    formName: FromRenderer.Props['formName']
) => {
    const mergedUnit: { [name: string]: Unit.UnitUnion } = {
        ...unitsHtml(fieldsMerger(fields, fieldsValidatorResult)),
        ...unitsForm,
    }

    return row.units
        .filter(column => !column.hide)
        .map((column, i) => {
            const unit = findUnit(mergedUnit, column.name)
            switch (unit.unitType) {
                case 'form':
                    return Object.values(fields)
                        .filter(f => f.name === column.name)
                        .filter(f => f.use === 'yep')
                        .map(f => (
                            <UnitFormRenderer
                                key={f.name}
                                field={f}
                                validatorResult={pipe(
                                    O.fromNullable(fieldsValidatorResult[f.name]),
                                    O.match(
                                        () => ({
                                            name: f.name,
                                            isValid: 'unknown',
                                            isValidating: true,
                                        }),
                                        identity
                                    )
                                )}
                                unit={unitsForm[f.name]}
                                onChange={onChange(f.name)}
                                view={column.view}
                                formName={formName}
                            />
                        ))
                case 'html':
                    return <UnitHtmlRenderer key={i} unit={unit} view={column.view} />
            }
        })
}

const alert = (type: Alert.AlertType) => <Alert type={type}>Errore durante il salvataggio, riprova.</Alert>

const renderAlert = (resource: FromRenderer.Props['resource']) => {
    if (!resource) {
        return null
    }

    const r = resource.state.resource
    switch (r.status) {
        case 'rejected':
            if (resource.state.canReject) {
                return pipe(
                    O.fromNullable((resource as formResource<unknown, unknown, true>).onRejected),
                    O.match<(resources: ResourceRejected<unknown>) => React.ReactNode, React.ReactNode>(
                        () => alert('info'),
                        f => f(r)
                    )
                )
            }
            return null
        case 'fail':
            return alert('danger')
        case 'fulfilled':
        case 'init':
        case 'submitted':
            return null
    }
}

const renderSubmit = (submit: FromRenderer.Props['submit']) => {
    switch (submit.type) {
        case 'none':
            return null
        case 'std':
            return (
                <FlexView gutters={{ lr: 'sm' }} hAlignContent="right">
                    <Divider />
                    <FlexView.Item>
                        <Button styleType="success">Salva »</Button>
                    </FlexView.Item>
                </FlexView>
            )
        case 'custom':
            return submit.submit
    }
}

interface sanitizedHelp {
    [k: string]: Exclude<helper<string>['string'], undefined>
}

const findUnitHelper = (unitsName: string[], hlp: O.Option<helper<string>>) =>
    pipe(
        hlp,
        O.map(h =>
            Object.keys(h)
                .filter(k => unitsName.includes(k))
                .reduce((c, k) => ({ ...c, [k]: h[k] as sanitizedHelp['string'] }), {} as sanitizedHelp)
        ),
        O.map(h =>
            Object.keys(h).reduce(
                (c, k) => ({ ...c, ...(typeof h[k] !== 'undefined' ? { [k]: h[k] } : {}) }),
                {} as sanitizedHelp
            )
        ),
        O.filter(h => Object.keys(h).length > 0),
        O.map((h): FormRow.Props['helpers'] | undefined =>
            Object.values(h).filter(hh => hh.forceSquash).length > 0
                ? pipe(
                      O.fromNullable(unitsName.map(n => h[n]).filter(hh => typeof hh !== 'undefined')[0]),
                      O.map((hh): FormRow.Props['helpers'] => ({ type: 'single', help: hh })),
                      O.toUndefined
                  )
                : { type: 'multiple', helps: h }
        ),
        O.toUndefined
    )

const renderBody = (props: FromRenderer.Props): React.ReactElement => {
    const fieldsMerged = fieldsMerger(props.fields, props.fieldsValidatorResult)
    const hlp = pipe(
        O.fromNullable(props.helper),
        O.map(h => h(props.employeesStatus, fieldsMerged))
    )

    return (
        <React.Fragment>
            {renderAlert(props.resource)}
            <Form
                name={props.formName}
                onSubmit={props.onSubmit}
                {...props.formStyle}
                {...buildTestSelector(`form-${props.formName}`)}
            >
                {props
                    .structure(props.employeesStatus, fieldsMerged)
                    .filter(p => !p.hide)
                    .map((panel, i) => (
                        <React.Fragment key={i}>
                            {pipe(
                                O.fromNullable(panel.label),
                                map(l => (typeof l === 'string' ? <Title tag="h2">{panel.label}</Title> : l)),
                                O.toNullable
                            )}
                            {panel.rows.map((row, index) => {
                                if (row.units.filter(u => !u.hide).length === 0) {
                                    return null
                                }
                                return (
                                    <FormRow
                                        key={index}
                                        helpers={findUnitHelper(
                                            row.units.map(u => u.name),
                                            hlp
                                        )}
                                    >
                                        <FlexView {...flexViewProps(row)}>
                                            {renderRow(
                                                row,
                                                props.unitsForm,
                                                props.unitsHtml,
                                                props.fields,
                                                props.fieldsValidatorResult,
                                                props.onChange,
                                                props.formName
                                            )}
                                        </FlexView>
                                    </FormRow>
                                )
                            })}
                        </React.Fragment>
                    ))}
                {renderSubmit(props.submit)}
            </Form>
        </React.Fragment>
    )
}

export const FromRenderer: React.FunctionComponent<FromRenderer.Props> = props =>
    pipe(
        O.fromNullable(props.resource),
        O.match(
            (): React.ReactElement => renderBody(props),
            (r): React.ReactElement => masterStateBuilder([r.state], () => renderBody(props)) as React.ReactElement
        )
    )
